import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Refresh, Save } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { superAdminApi } from '../../../api/superAdminApi';
import { TenantDto, TenantFeatureDto, TenantFeatureSettingsDto } from '../../../types/superAdminTypes';

const FeatureFlagsPage: React.FC = () => {
  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [settings, setSettings] = useState<TenantFeatureSettingsDto | null>(null);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const groupedFeatures = useMemo(() => {
    const groups = new Map<string, TenantFeatureDto[]>();
    for (const feature of settings?.features ?? []) {
      const module = feature.module || 'General';
      groups.set(module, [...(groups.get(module) ?? []), feature]);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [settings]);

  const enabledCount = settings?.features.filter((feature) => feature.isEnabled).length ?? 0;
  const totalCount = settings?.features.length ?? 0;

  const loadTenants = useCallback(async () => {
    setLoadingTenants(true);
    setError(null);
    try {
      const data = await superAdminApi.getTenants();
      setTenants(data);
      setSelectedTenantId((current) => current || data[0]?.id || '');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load tenants');
    } finally {
      setLoadingTenants(false);
    }
  }, []);

  const loadFeatures = useCallback(async (tenantId: string) => {
    if (!tenantId) {
      setSettings(null);
      return;
    }

    setLoadingFeatures(true);
    setError(null);
    try {
      const data = await superAdminApi.getTenantFeatures(tenantId);
      setSettings(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load tenant features');
    } finally {
      setLoadingFeatures(false);
    }
  }, []);

  useEffect(() => {
    void loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    void loadFeatures(selectedTenantId);
  }, [selectedTenantId, loadFeatures]);

  const handleToggle = (featureKey: string, isEnabled: boolean) => {
    setSettings((current) => {
      if (!current) return current;
      return {
        ...current,
        features: current.features.map((feature) =>
          feature.featureKey === featureKey ? { ...feature, isEnabled } : feature
        ),
      };
    });
  };

  const setAllFeatures = (isEnabled: boolean) => {
    setSettings((current) => {
      if (!current) return current;
      return {
        ...current,
        features: current.features.map((feature) => ({ ...feature, isEnabled })),
      };
    });
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    setError(null);
    try {
      await superAdminApi.saveTenantFeatures(settings.tenantId, {
        features: settings.features.map((feature) => ({
          featureKey: feature.featureKey,
          isEnabled: feature.isEnabled,
        })),
      });
      toast.success('Tenant features updated');
      await loadFeatures(settings.tenantId);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save tenant features');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            Feature Flags
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Control tenant module access across the HRMS application
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => loadFeatures(selectedTenantId)} disabled={!selectedTenantId || loadingFeatures}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={!settings || saving}>
            Save Changes
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <FormControl sx={{ minWidth: { xs: '100%', md: 340 } }}>
            <InputLabel>Tenant</InputLabel>
            <Select
              label="Tenant"
              value={selectedTenantId}
              onChange={(event) => setSelectedTenantId(event.target.value)}
              disabled={loadingTenants}
            >
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.companyName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {settings && (
            <Chip
              color={enabledCount === totalCount ? 'success' : 'primary'}
              label={`${enabledCount} of ${totalCount} features enabled`}
              sx={{ width: 'fit-content' }}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={() => setAllFeatures(true)} disabled={!settings}>
              Enable All
            </Button>
            <Button size="small" variant="outlined" color="warning" onClick={() => setAllFeatures(false)} disabled={!settings}>
              Disable All
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {loadingFeatures ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : !settings ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">Select a tenant to manage feature access.</Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {groupedFeatures.map(([module, features]) => (
            <Paper key={module} sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={700}>{module}</Typography>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                {features.map((feature) => (
                  <Box
                    key={feature.featureKey}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      px: 1.5,
                      py: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={600}>{feature.displayName}</Typography>
                      <Typography variant="caption" color="text.secondary">{feature.featureKey}</Typography>
                    </Box>
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Switch
                          checked={feature.isEnabled}
                          onChange={(event) => handleToggle(feature.featureKey, event.target.checked)}
                        />
                      }
                      label={feature.isEnabled ? 'Enabled' : 'Disabled'}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default FeatureFlagsPage;
